import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Check, Crown, Zap, X, Tag, Loader, RefreshCw, Sparkles } from 'lucide-react';
import axios from 'axios';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const BILLING_OPTS = [
  { key: 'monthly', label: '1 Month' },
  { key: '3mo',     label: '3 Months', badge: 'POPULAR' },
  { key: '6mo',     label: '6 Months', badge: 'SAVE 25%' },
  { key: '1yr',     label: '1 Year',   badge: 'SAVE 33%' },
];

const PREMIUM_PRICE = { monthly: 599, '3mo': 500, '6mo': 450, '1yr': 400 };
const PREMIUM_TOTAL = { monthly: 599, '3mo': 1499, '6mo': 2699, '1yr': 4799 };

const PLAN_FEATURES = {
  free: [
    '₹10L virtual capital',
    'Basic discipline score',
    '20 trades per month',
    'ACT scenarios (12 exercises)',
    'Risk profile setup',
    'Nyx pre-trade check (5 msg/week)',
    'Markets, Global Cues & Regime',
    'Learning Path',
  ],
  premium: [
    'Everything in Free',
    'Unlimited trades per month',
    'Full discipline score (9 components)',
    'Nyx AI Coach (150 msg/week)',
    'Behavior & Trade Analytics',
    'Market Scanners (27+)',
    'Algo Flows (unlimited)',
    'Backtesting (unlimited)',
    'PDF & Email trade reports',
  ],
};

const TIER_COLOR = { free: '#6b7280', premium: '#6366f1', admin: '#10b981' };
const BILLING_LABEL = { monthly: '1 Month', '3mo': '3 Months', '6mo': '6 Months', '1yr': '1 Year' };
const SOURCE_LABEL = { razorpay: 'Paid · Razorpay', manual: 'Manually activated' };

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload  = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function SubscriptionSection({ onBack }) {
  const { colors } = useTheme();
  const { user } = useAuth();

  const [billing,      setBilling]      = useState('monthly');
  const [subStatus,    setSubStatus]    = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [cancelBusy,   setCancelBusy]   = useState(false);
  const [error,        setError]        = useState('');
  const [showCancel,   setShowCancel]   = useState(false);
  const [successMsg,   setSuccessMsg]   = useState('');
  const [promoInput,   setPromoInput]   = useState('');
  const [promoResult,  setPromoResult]  = useState(null);
  const [promoLoading,  setPromoLoading]  = useState(false);
  const [promoError,    setPromoError]    = useState('');
  const [planHistory,   setPlanHistory]   = useState([]);

  const currentTier = user?.plan || 'free';
  const ac = colors.accent || '#6366F1';

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }
    axios.get(`${API_URL}/subscriptions/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setSubStatus(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    axios.get(`${API_URL}/subscriptions/history`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setPlanHistory(r.data?.history || []))
      .catch(() => {});
  }, []);

  const handleBillingChange = (key) => {
    setBilling(key);
    setPromoResult(null);
    setPromoError('');
  };

  const applyPromo = useCallback(async () => {
    if (!promoInput.trim()) return;
    setPromoError('');
    setPromoResult(null);
    setPromoLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API_URL}/subscriptions/validate-code`,
        { code: promoInput.trim(), tier: 'premium', billing },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPromoResult(data);
    } catch (err) {
      setPromoError(err?.response?.data?.detail || 'Invalid promo code');
    } finally {
      setPromoLoading(false);
    }
  }, [promoInput, billing]);

  const handleUpgrade = useCallback(async () => {
    setError('');
    setCheckoutBusy(true);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error('Could not load Razorpay. Check your connection.');
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API_URL}/subscriptions/create`,
        { tier: 'premium', billing, promo_code: promoResult?.code || undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const options = {
        key: data.key_id,
        subscription_id: data.subscription_id,
        name: 'TradeReflex',
        description: 'Premium Plan',
        image: '/logo192.png',
        prefill: { email: user?.email || '', name: user?.full_name || '' },
        theme: { color: '#6366F1' },
        handler: () => {
          setSuccessMsg('Payment successful! Your Premium plan will activate shortly.');
          setCheckoutBusy(false);
        },
        modal: { ondismiss: () => setCheckoutBusy(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Something went wrong');
      setCheckoutBusy(false);
    }
  }, [billing, user, promoResult]);

  const handleCancel = async () => {
    setCancelBusy(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/subscriptions/cancel`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setShowCancel(false);
      setSuccessMsg('Your subscription will cancel at the end of the current billing period.');
      setSubStatus(prev => prev ? { ...prev, subscription: { ...prev.subscription, status: 'cancelling' } } : prev);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to cancel');
    } finally {
      setCancelBusy(false);
    }
  };

  // ── Active plan card ───────────────────────────────────────────────────────
  const ActivePlanCard = () => {
    const sub = subStatus?.subscription;
    const PAID_STATUSES = ['active', 'authenticated', 'cancelling'];
    const isActiveSub = sub && PAID_STATUSES.includes(sub.status);

    const tc         = currentTier === 'premium' ? '#6366f1' : '#10b981';
    const tierLabel  = currentTier === 'premium' ? 'Premium' : currentTier === 'admin' ? 'Admin' : 'Free';
    const billingLbl = isActiveSub ? (BILLING_LABEL[sub.billing] || '') : '';
    const features   = PLAN_FEATURES[currentTier] || PLAN_FEATURES.premium;
    const renewDate  = isActiveSub && sub.current_period_end
      ? new Date(sub.current_period_end).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
      : user?.plan_expires_at
        ? new Date(user.plan_expires_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
        : currentTier === 'admin' ? 'Lifetime' : currentTier === 'free' ? 'No expiry' : null;
    const isCancelling = sub?.status === 'cancelling';
    const TierIcon     = currentTier === 'premium' ? Zap : Crown;
    const userName     = user?.full_name || user?.name || '';
    const userEmail    = user?.email || '';

    return (
      <div style={{
        position: 'relative', overflow: 'hidden', borderRadius: 18,
        background: `linear-gradient(145deg, ${tc}1a 0%, ${tc}08 50%, transparent 100%)`,
        border: `1px solid ${tc}45`,
      }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, transparent 0%, ${tc} 40%, ${tc} 60%, transparent 100%)` }} />
        <div style={{ position: 'absolute', top: -40, right: -40, width: 120, height: 120, borderRadius: '50%', background: `radial-gradient(circle, ${tc}18 0%, transparent 70%)`, pointerEvents: 'none' }} />

        <div style={{ padding: '20px 18px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
              <div style={{ width: 42, height: 42, borderRadius: 12, background: `linear-gradient(135deg, ${tc}30, ${tc}14)`, border: `1.5px solid ${tc}50`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <TierIcon style={{ width: 19, height: 19, color: tc }} />
              </div>
              <div>
                {userName && <p style={{ color: colors.primaryText, fontSize: 15, fontWeight: 800, margin: '0 0 1px' }}>{userName}</p>}
                {userEmail && <p style={{ color: colors.secondaryText, fontSize: 12, margin: 0 }}>{userEmail}</p>}
              </div>
            </div>
            <span style={{
              background: isCancelling ? 'rgba(239,68,68,0.12)' : `${tc}18`,
              color: isCancelling ? '#ef4444' : tc,
              border: `1px solid ${isCancelling ? '#ef444430' : `${tc}35`}`,
              borderRadius: 20, padding: '3px 11px', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
            }}>
              {isCancelling ? 'CANCELLED' : 'ACTIVE'}
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
            <div style={{ padding: '10px 12px', borderRadius: 12, background: `${tc}0d`, border: `1px solid ${tc}22` }}>
              <p style={{ color: colors.secondaryText, fontSize: 11, margin: '0 0 3px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Plan</p>
              <p style={{ color: colors.primaryText, fontSize: 15, fontWeight: 800, margin: '0 0 1px' }}>{tierLabel}</p>
              {billingLbl && <p style={{ color: tc, fontSize: 11, fontWeight: 600, margin: 0 }}>{billingLbl} billing</p>}
            </div>
            <div style={{ padding: '10px 12px', borderRadius: 12, background: isCancelling ? 'rgba(239,68,68,0.06)' : `${tc}0d`, border: `1px solid ${isCancelling ? '#ef444422' : `${tc}22`}` }}>
              <p style={{ color: colors.secondaryText, fontSize: 11, margin: '0 0 3px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {isCancelling ? 'Access Until' : currentTier === 'admin' ? 'Access' : currentTier === 'free' ? 'Plan' : renewDate ? 'Expires On' : 'Expiry'}
              </p>
              <p style={{ color: isCancelling ? '#ef4444' : colors.primaryText, fontSize: 13, fontWeight: 800, margin: 0, lineHeight: 1.3 }}>
                {renewDate || '—'}
              </p>
            </div>
          </div>

          {/* Plan source badge */}
          <div style={{ marginBottom: 14 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
              background: user?.plan_source === 'razorpay' ? 'rgba(22,199,132,0.1)' : 'rgba(99,102,241,0.1)',
              border: `1px solid ${user?.plan_source === 'razorpay' ? 'rgba(22,199,132,0.3)' : 'rgba(99,102,241,0.25)'}`,
              color: user?.plan_source === 'razorpay' ? '#16C784' : tc,
            }}>
              {user?.plan_source === 'razorpay' ? '✓ ' : '⚙ '}
              {SOURCE_LABEL[user?.plan_source] || 'Manually activated'}
            </span>
          </div>

          <div style={{ marginBottom: 14 }}>
            <p style={{ color: colors.secondaryText, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 8px' }}>What's included</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px 8px' }}>
              {features.slice(0, 6).map(f => (
                <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 14, height: 14, borderRadius: '50%', background: `${tc}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Check style={{ width: 8, height: 8, color: tc }} />
                  </div>
                  <span style={{ fontSize: 11, color: colors.secondaryText, lineHeight: 1.4 }}>{f}</span>
                </div>
              ))}
            </div>
          </div>

          {!isCancelling && sub?.status === 'active' && (
            <button onClick={() => setShowCancel(true)} style={{
              width: '100%', padding: '8px 0', borderRadius: 10, fontSize: 12, fontWeight: 600,
              cursor: 'pointer', background: 'transparent', border: '1px solid rgba(239,68,68,0.2)',
              color: '#ef4444',
            }}>
              Cancel Subscription
            </button>
          )}
          {isCancelling && (
            <p style={{ color: '#6b7280', fontSize: 11, margin: 0, textAlign: 'center', lineHeight: 1.5 }}>
              You'll keep full access until {renewDate}. No further charges.
            </p>
          )}
        </div>
      </div>
    );
  };

  // ── Plan card ──────────────────────────────────────────────────────────────
  const PlanCard = ({ tier, highlighted }) => {
    const isFree    = tier === 'free';
    const isCurrent = currentTier === tier;
    const price     = isFree ? 0 : PREMIUM_PRICE[billing];
    const total     = isFree ? 0 : PREMIUM_TOTAL[billing];
    const features  = PLAN_FEATURES[tier];
    const TierIcon  = isFree ? Crown : Zap;
    const tc        = TIER_COLOR[tier] || '#6b7280';

    return (
      <div style={{
        border: `1px solid ${tc}${highlighted ? '50' : '22'}`,
        borderRadius: 18,
        background: `linear-gradient(145deg, ${tc}${highlighted ? '16' : '0a'} 0%, ${tc}05 60%, transparent 100%)`,
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: highlighted ? 2 : 1, background: `linear-gradient(90deg, transparent 0%, ${tc}${highlighted ? 'cc' : '55'} 40%, ${tc}${highlighted ? 'cc' : '55'} 60%, transparent 100%)` }} />
        <div style={{ position: 'absolute', top: -30, right: -30, width: 100, height: 100, borderRadius: '50%', background: `radial-gradient(circle, ${tc}${highlighted ? '18' : '0d'} 0%, transparent 70%)`, pointerEvents: 'none' }} />

        {isCurrent && (
          <div style={{
            position: 'absolute', top: 12, right: 12,
            background: `${tc}22`, border: `1px solid ${tc}50`,
            borderRadius: 20, padding: '3px 10px',
            fontSize: 10, fontWeight: 700, color: tc, letterSpacing: '0.08em',
          }}>YOUR PLAN</div>
        )}

        <div style={{ padding: '18px 16px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: `linear-gradient(135deg, ${tc}28, ${tc}12)`, border: `1.5px solid ${tc}45`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <TierIcon style={{ width: 15, height: 15, color: tc }} />
            </div>
            <div>
              <span style={{ fontSize: 14, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: tc }}>
                {isFree ? 'Free' : 'Premium'}
              </span>
              {highlighted && !isCurrent && (
                <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: '#16C784', background: 'rgba(22,199,132,0.12)', border: '1px solid rgba(22,199,132,0.3)', borderRadius: 6, padding: '1px 7px' }}>RECOMMENDED</span>
              )}
            </div>
          </div>

          {isFree ? (
            <div style={{ marginBottom: 16, paddingBottom: 14, borderBottom: `1px solid ${tc}18` }}>
              <span style={{ fontSize: 34, fontWeight: 900, color: colors.primaryText }}>₹0</span>
              <span style={{ fontSize: 13, color: colors.secondaryText, marginLeft: 4 }}>/ forever</span>
            </div>
          ) : (
            <div style={{ marginBottom: 16, paddingBottom: 14, borderBottom: `1px solid ${tc}18` }}>
              {promoResult ? (
                <div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 2 }}>
                    <span style={{ fontSize: 34, fontWeight: 900, color: '#16C784' }}>₹{promoResult.discounted_amount}</span>
                    <span style={{ fontSize: 13, color: colors.secondaryText, textDecoration: 'line-through' }}>₹{price}</span>
                    <span style={{ fontSize: 12, color: colors.secondaryText }}>/mo</span>
                  </div>
                  <span style={{ fontSize: 11, color: '#16C784', background: 'rgba(22,199,132,0.1)', border: '1px solid rgba(22,199,132,0.25)', borderRadius: 6, padding: '2px 8px' }}>{promoResult.discount_pct}% off applied</span>
                </div>
              ) : (
                <div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                    <span style={{ fontSize: 30, fontWeight: 900, color: colors.primaryText }}>₹{price}</span>
                    <span style={{ fontSize: 12, color: colors.secondaryText }}>/mo</span>
                  </div>
                  {billing !== 'monthly' && (
                    <p style={{ fontSize: 11, color: colors.secondaryText, marginTop: 2 }}>₹{total.toLocaleString()} billed every {BILLING_LABEL[billing]}</p>
                  )}
                </div>
              )}
            </div>
          )}

          <ul style={{ margin: '0 0 16px', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 7 }}>
            {features.map(f => (
              <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ width: 16, height: 16, borderRadius: '50%', background: `${tc}20`, border: `1px solid ${tc}35`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                  <Check style={{ width: 8, height: 8, color: tc }} />
                </div>
                <span style={{ fontSize: 12, color: colors.secondaryText, lineHeight: 1.5 }}>{f}</span>
              </li>
            ))}
          </ul>

          {isFree ? (
            <button disabled style={{
              width: '100%', padding: '10px 0', borderRadius: 11, fontSize: 13, fontWeight: 600,
              border: '1px solid rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.2)',
              background: 'transparent', cursor: 'default',
            }}>
              {isCurrent ? 'Your current plan' : 'Free — No payment needed'}
            </button>
          ) : isCurrent ? (
            <div style={{ padding: '10px', borderRadius: 11, background: `${tc}10`, border: `1px solid ${tc}30`, textAlign: 'center' }}>
              <span style={{ color: tc, fontSize: 13, fontWeight: 700 }}>✓ Active Plan</span>
            </div>
          ) : (
            <button onClick={handleUpgrade} disabled={checkoutBusy} style={{
              width: '100%', padding: '11px 0', borderRadius: 11, fontSize: 13, fontWeight: 700,
              cursor: checkoutBusy ? 'wait' : 'pointer',
              background: `linear-gradient(135deg, ${tc}ee, ${tc}99)`,
              border: 'none', color: '#fff',
              opacity: checkoutBusy ? 0.7 : 1,
              boxShadow: `0 4px 18px ${tc}35`,
              letterSpacing: '0.02em',
            }}>
              {checkoutBusy ? 'Loading…' : 'Upgrade to Premium'}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingBottom: 32 }}>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={onBack} style={{ background: `${ac}14`, border: `1px solid ${ac}22`, borderRadius: 8, padding: 6, cursor: 'pointer', display: 'flex' }}>
          <ArrowLeft style={{ width: 16, height: 16, color: colors.primaryText }} />
        </button>
        <div>
          <h1 style={{ fontSize: 17, fontWeight: 700, color: colors.primaryText, margin: 0 }}>Subscription</h1>
          <p style={{ fontSize: 12, color: colors.secondaryText, margin: 0 }}>Manage your plan and billing</p>
        </div>
      </div>

      {successMsg && (
        <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(22,199,132,0.1)', border: '1px solid rgba(22,199,132,0.3)', color: '#16C784', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {successMsg}
          <button onClick={() => setSuccessMsg('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#16C784', padding: 0 }}><X style={{ width: 14, height: 14 }} /></button>
        </div>
      )}
      {error && (
        <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {error}
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 0 }}><X style={{ width: 14, height: 14 }} /></button>
        </div>
      )}

      {!loading && <ActivePlanCard />}

      {/* Plan history */}
      {planHistory.length > 0 && (
        <div>
          <p style={{ fontSize: 12, color: colors.secondaryText, margin: '0 0 8px', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Plan History</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {planHistory.map((h, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderRadius: 10, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 2 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: colors.primaryText, textTransform: 'capitalize' }}>
                      {h.old_plan || 'free'} → {h.new_plan}
                    </span>
                    <span style={{
                      fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 10,
                      background: h.source === 'razorpay' ? 'rgba(22,199,132,0.1)' : 'rgba(99,102,241,0.1)',
                      color: h.source === 'razorpay' ? '#16C784' : '#818cf8',
                      border: `1px solid ${h.source === 'razorpay' ? 'rgba(22,199,132,0.25)' : 'rgba(99,102,241,0.25)'}`,
                    }}>
                      {SOURCE_LABEL[h.source] || h.source}
                    </span>
                  </div>
                  {h.note && <span style={{ fontSize: 11, color: colors.secondaryText }}>{h.note}</span>}
                </div>
                <span style={{ fontSize: 11, color: colors.secondaryText, flexShrink: 0, marginLeft: 8 }}>
                  {new Date(h.changed_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Billing toggle */}
      <div>
        <p style={{ fontSize: 12, color: colors.secondaryText, margin: '0 0 8px', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Billing Period</p>
        <div style={{ display: 'flex', gap: 6, padding: 4, borderRadius: 12, background: colors.inputBg || '#0f0f0f', border: '1px solid rgba(255,255,255,0.07)' }}>
          {BILLING_OPTS.map(opt => (
            <button key={opt.key} onClick={() => handleBillingChange(opt.key)} style={{
              flex: 1, padding: '6px 4px', borderRadius: 8,
              fontSize: 11, fontWeight: 600, cursor: 'pointer',
              background: billing === opt.key ? ac : 'transparent',
              border: 'none', color: billing === opt.key ? '#fff' : colors.secondaryText,
              transition: 'all 0.15s',
            }}>
              {opt.badge ? (
                <>
                  <span>{opt.label}</span>
                  <span style={{ display: 'block', fontSize: 9, fontWeight: 700, color: billing === opt.key ? 'rgba(255,255,255,0.75)' : '#16C784' }}>{opt.badge}</span>
                </>
              ) : opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Promo code */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <p style={{ fontSize: 12, color: colors.secondaryText, margin: 0, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Promo Code</p>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '0 12px', borderRadius: 10, background: colors.inputBg || '#0f0f0f', border: `1px solid ${promoResult ? 'rgba(22,199,132,0.4)' : promoError ? 'rgba(239,68,68,0.4)' : 'rgba(255,255,255,0.1)'}` }}>
            <Tag style={{ width: 13, height: 13, color: promoResult ? '#16C784' : colors.secondaryText, flexShrink: 0 }} />
            <input
              value={promoInput}
              onChange={e => { setPromoInput(e.target.value.toUpperCase()); setPromoResult(null); setPromoError(''); }}
              onKeyDown={e => { if (e.key === 'Enter' && promoInput.trim()) applyPromo(); }}
              placeholder="Enter promo code"
              style={{ flex: 1, background: 'none', border: 'none', outline: 'none', fontSize: 13, color: colors.primaryText, padding: '10px 0', letterSpacing: '0.08em' }}
            />
            {(promoInput || promoResult) && (
              <button onClick={() => { setPromoInput(''); setPromoResult(null); setPromoError(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: colors.secondaryText }}>
                <X style={{ width: 13, height: 13 }} />
              </button>
            )}
          </div>
          <button onClick={applyPromo} disabled={!promoInput.trim() || promoLoading}
            style={{ padding: '0 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: promoInput.trim() ? 'pointer' : 'default', background: ac, border: 'none', color: '#fff', opacity: !promoInput.trim() ? 0.4 : 1, minWidth: 70 }}>
            {promoLoading ? <Loader style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} /> : 'Apply'}
          </button>
        </div>

        {promoResult && (
          <div style={{ padding: '10px 12px', borderRadius: 10, background: 'rgba(22,199,132,0.08)', border: '1px solid rgba(22,199,132,0.25)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <Sparkles style={{ width: 13, height: 13, color: '#16C784', flexShrink: 0 }} />
              <span style={{ color: '#16C784', fontWeight: 700, fontSize: 13 }}>{promoResult.discount_pct}% off applied</span>
              <span style={{ color: colors.secondaryText, fontSize: 12 }}>— You save ₹{promoResult.savings}</span>
            </div>
            <p style={{ color: '#4b5563', fontSize: 11, margin: 0, paddingLeft: 19 }}>
              Discount on first cycle, then renews at full price
            </p>
          </div>
        )}
        {promoError && <p style={{ fontSize: 12, color: '#ef4444', margin: 0, paddingLeft: 4 }}>{promoError}</p>}
      </div>

      {/* Plan cards */}
      <div>
        <p style={{ fontSize: 12, color: colors.secondaryText, margin: '0 0 10px', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Choose a Plan</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <PlanCard tier="free" highlighted={false} />
          <PlanCard tier="premium" highlighted={true} />
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', borderRadius: 10, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <RefreshCw style={{ width: 13, height: 13, color: '#4b5563', flexShrink: 0, marginTop: 1 }} />
        <p style={{ color: '#4b5563', fontSize: 11, margin: 0, lineHeight: 1.6 }}>
          Auto-renews at the end of each billing period. You'll receive a reminder from Razorpay before each charge. Cancel anytime — you keep access until the period ends.
        </p>
      </div>

      {showCancel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}
          onClick={() => setShowCancel(false)}>
          <div style={{ width: '100%', maxWidth: 340, borderRadius: 16, padding: 24, background: colors.cardBg, border: '1px solid rgba(239,68,68,0.25)' }}
            onClick={e => e.stopPropagation()}>
            <p style={{ fontSize: 15, fontWeight: 700, color: colors.primaryText, marginBottom: 6 }}>Cancel subscription?</p>
            <p style={{ fontSize: 13, color: colors.secondaryText, marginBottom: 20, lineHeight: 1.6 }}>
              You'll keep full access until the end of your current billing period. After that your account moves to the Free plan — no further charges.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowCancel(false)} style={{ flex: 1, padding: '10px 0', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: 'transparent', border: `1px solid ${colors.border}`, color: colors.primaryText }}>
                Keep Plan
              </button>
              <button onClick={handleCancel} disabled={cancelBusy} style={{ flex: 1, padding: '10px 0', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: cancelBusy ? 'wait' : 'pointer', background: '#ef4444', border: 'none', color: '#fff', opacity: cancelBusy ? 0.7 : 1 }}>
                {cancelBusy ? 'Cancelling…' : 'Yes, Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
